import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { maskRouteTokens } from './reportRoute';

/**
 * "Report a translation" (#2120): the in-app path for a family or a TRF
 * reviewer to flag a string on a translated page. Before this, the only
 * report path was a chat message's ViewOriginalModal; every other translated
 * screen had none.
 *
 * Two triggers share this one dialog: a text action on the
 * MachineTranslationBanner and an icon action beside the LanguageSwitcher.
 * Each calls `useReportTranslation()` and renders its own button.
 *
 * What a report carries (POST /api/translation-reports, `kind: 'ui'`):
 *   - `route`: `window.location.pathname` only, with any token in the path
 *     masked (`/provider-upload/<token>` → `/provider-upload/:token`, and any
 *     long opaque segment → `:token`; see `reportRoute.ts`). No query or hash,
 *     because magic-link and reset tokens live there. The server strips the
 *     query and hash and masks the path again.
 *   - `locale`: the language the page is showing.
 *   - `selected_text`: what the person highlighted, read through
 *     `window.getSelection()` at the moment they press the trigger.
 *   - `note`: their free text, the only thing they type.
 * Reporter id and role come from the session on the server, never the body.
 *
 * Send is the confirm step: nothing is posted until the person presses it,
 * and the dialog then shows a thank-you state in place of the form. A failed
 * send keeps the form and the note, and says so.
 */

type Phase = 'form' | 'sending' | 'sent';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedText: string;
  locale: string;
  noteMax: number;
}

export function ReportTranslationDialog({ open, onOpenChange, selectedText, locale, noteMax }: DialogProps) {
  const { t } = useTranslation();
  const noteId = useId();
  const [note, setNote] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [failed, setFailed] = useState(false);

  const canSend = phase === 'form' && (selectedText.length > 0 || note.trim().length > 0);

  async function send() {
    if (!canSend) return;
    setPhase('sending');
    setFailed(false);
    try {
      await apiClient.post('/translation-reports', {
        kind: 'ui',
        route: maskRouteTokens(window.location.pathname),
        locale,
        ...(selectedText ? { selected_text: selectedText } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setPhase('sent');
    } catch {
      setFailed(true);
      setPhase('form');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {phase === 'sent' ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('language.report.thanksTitle')}</DialogTitle>
              <DialogDescription>{t('language.report.thanksBody')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('actions.close')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <DialogHeader>
              <DialogTitle>{t('language.report.title')}</DialogTitle>
              <DialogDescription>{t('language.report.lede')}</DialogDescription>
            </DialogHeader>

            {selectedText ? (
              <div className="grid gap-1">
                <p className="m-0 text-label font-medium text-stevens-ink-3">{t('language.report.selectedLabel')}</p>
                <blockquote
                  data-testid="report-selected-text"
                  className="m-0 whitespace-pre-wrap break-words rounded-md border border-stevens-rule bg-muted/40 px-3 py-2 text-body-sm"
                >
                  {selectedText}
                </blockquote>
              </div>
            ) : (
              <p className="m-0 text-body-sm text-stevens-ink-3">{t('language.report.noSelection')}</p>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor={noteId}>{t('language.report.noteLabel')}</Label>
              <Textarea
                id={noteId}
                value={note}
                maxLength={noteMax}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {failed && (
              <p role="alert" className="m-0 text-body-sm text-status-danger-fg">
                {t('language.report.failed')}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={!canSend}>
                {phase === 'sending' ? t('language.report.sending') : t('language.report.send')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
